import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useParams } from "react-router";
import CasesTable from "../components/Cases/CasesTable";
import DeleteCasesTable from "../components/Cases/DeleteCasesTable";
import { useState } from "react";
import { DeleteCaseRequest } from "../models/Jobs/JobRequest";
import CreateExportCasesToCsvTable from "../components/Cases/CreateExportCasesToCsvTable";
import { Case } from "../models/Case";
import ExportCasesToCsvStatusTable from "../components/Cases/ExportCasesToCsvStatusTable";
import {UserRole} from "../models/User";
import { Settings2, X } from "lucide-react";
import { useManagementDrawer } from "../hooks/useManagementDrawer";

export enum ActiveCaseJobsExplorer {
  DeleteCases = 0,
  ExportCsv = 1,
}

interface CasesPageProps {
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CasesPage: React.FC<CasesPageProps> = ({
  userRole,
  isAssignedUser
}) => {
  const { organizationId } = useParams();
  const [newCasesForDeletion, setNewCasesForDeletion] = useState<DeleteCaseRequest[]>([]);
  const [newCasesForExport, setNewCasesForExport] = useState<Case[]>([]);
  const [selectedTab, setSelectedTab] = useState<ActiveCaseJobsExplorer>(ActiveCaseJobsExplorer.ExportCsv);
  const queuedCount = newCasesForDeletion.length + newCasesForExport.length;
  const [drawerOpen, setDrawerOpen] = useManagementDrawer(queuedCount);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="flex my-2 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between shrink-0">
        <h2 className="text-3xl font-bold tracking-tight">Cases</h2>
        {userRole !== UserRole.Viewer && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className="gap-2"
          >
            <Settings2 className="size-4" />
            Manage
            {queuedCount > 0 && (
              <Badge variant="secondary" className="ml-1 px-1.5">
                {queuedCount}
              </Badge>
            )}
          </Button>
        )}
      </div>
      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        <div className="flex flex-col min-h-0 flex-1 min-w-0">
          <CasesTable
            organizationId={organizationId ?? ""}
            activeJobsExplorer={selectedTab}
            newCasesForDeletion={newCasesForDeletion}
            setNewCasesForDeletion={setNewCasesForDeletion}
            newCasesForExport={newCasesForExport}
            setNewCasesForExport={setNewCasesForExport}
            userRole={userRole}
            isAssignedUser={isAssignedUser}
          />
        </div>
        {userRole !== UserRole.Viewer && drawerOpen && (
          <div className="flex flex-col min-h-0 w-full lg:w-96 shrink-0 rounded-md border bg-background shadow-sm">
            <div className="flex items-center justify-between border-b px-4 py-3 shrink-0">
              <h3 className="font-semibold text-foreground">Manage cases</h3>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close"
              >
                <X className="size-4" />
              </Button>
            </div>
            <div className="flex flex-col flex-1 min-h-0 p-4">
              <ToggleGroup
                type="single"
                value={String(selectedTab)}
                onValueChange={(v) => { if (v) setSelectedTab(Number(v) as ActiveCaseJobsExplorer); }}
                variant="outline"
                size="sm"
                className="w-full my-1"
              >
                {(userRole === UserRole.Admin || isAssignedUser) && (
                  <ToggleGroupItem value={String(ActiveCaseJobsExplorer.DeleteCases)} className="flex-1">
                    Delete cases
                  </ToggleGroupItem>
                )}
                <ToggleGroupItem value={String(ActiveCaseJobsExplorer.ExportCsv)} className="flex-1">
                  Export CSV
                </ToggleGroupItem>
              </ToggleGroup>
              <div className="flex-1 min-h-0 flex flex-col">
                {selectedTab === ActiveCaseJobsExplorer.DeleteCases && (userRole === UserRole.Admin || isAssignedUser) && (
                  <DeleteCasesTable
                    organizationId={organizationId ?? ""}
                    newCasesForDeletion={newCasesForDeletion}
                    setNewCasesForDeletion={setNewCasesForDeletion}
                  />
                )}
                {selectedTab === ActiveCaseJobsExplorer.ExportCsv && (
                  <div className="flex-1 overflow-auto min-h-0">
                    <div className="flex flex-col h-full gap-4">
                      {newCasesForExport.length > 0 && (
                        <>
                          <div className="flex items-center gap-3 py-2 pt-4">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>New</b></span>
                            <Separator className="flex-1" />
                          </div>
                          <CreateExportCasesToCsvTable
                            organizationId={organizationId ?? ""}
                            newCasesForExport={newCasesForExport}
                            setNewCasesForExport={setNewCasesForExport}
                            userRole={userRole}
                            isAssignedUser={isAssignedUser}
                          />
                          <div className="flex items-center gap-3 py-2">
                            <Separator className="flex-1" />
                            <span className="text-xs font-medium text-muted-foreground"><b>Existing exports</b></span>
                            <Separator className="flex-1" />
                          </div>
                        </>
                      )}
                      <ExportCasesToCsvStatusTable
                        organizationId={organizationId ?? ""}
                        userRole={userRole}
                        isAssignedUser={isAssignedUser}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CasesPage;
